import axios from "axios";
import { toast } from "react-toastify";
import BASE_URL from "../constants/baseUrls";

const REQUEST_TIMEOUT = 2 * 60 * 1000; // 2 minutes
const TOKEN_REFRESH_ENDPOINT = "/api/v2/login/refresh/";
const CLIENT_ID = "sMUGJXg7FBFEmMVwlr5EDe4XXELx7EWLqzc5FWuK";

//store user tokens and information in local storage
const storeUserTokens = ({ access_token, refresh_token, expires_in }, userInformation) => {
    localStorage.setItem("accessToken", access_token);
    localStorage.setItem("refreshToken", refresh_token);
    localStorage.setItem("expirationDate", expires_in);
    localStorage.setItem("userInformation", JSON.stringify(userInformation));
};

const isTokenExpired = () => {
    const expirationTime = localStorage.getItem("expirationDate");
    const expirationTimestamp = new Date(expirationTime).getTime();
    return Date.now() >= expirationTimestamp;
};

let isRefreshing = false;
let failedRequestsQueue = [];

const processFailedRequests = token => {
    failedRequestsQueue.forEach(promise => promise.resolve(token));
    failedRequestsQueue = [];
};

const addFailedRequest = request => {
    return new Promise((resolve, reject) => {
        failedRequestsQueue.push({ resolve, reject });
    })
        .then(token => {
            request.headers.Authorization = `Bearer ${token}`;
            return axios(request);
        })
        .catch(error => {
            return Promise.reject(error);
        });
};

const refreshAccessToken = async () => {
    if (isRefreshing) {
        return;
    }

    isRefreshing = true;
    const refreshToken = localStorage.getItem("refreshToken");

    try {
        const response = await axios.post(`${BASE_URL}${TOKEN_REFRESH_ENDPOINT}`, {
            refresh_token: refreshToken,
            client_id: CLIENT_ID,
        });
        storeUserTokens(response.data.data.auth, response.data?.data.user);
        processFailedRequests(response.data.data.auth.access_token);
        return response.data.data.auth.access_token;
    } catch (refreshError) {
        localStorage.clear()
        failedRequestsQueue.forEach(promise => promise.reject(refreshError));
        failedRequestsQueue = [];
        throw refreshError;
    } finally {
        isRefreshing = false;
    }
};

export const onRequestFulfilled = async request => {
    const accessToken = localStorage.getItem("accessToken");

    if (accessToken && isTokenExpired()) {
        if (isRefreshing) {
            return addFailedRequest(request);
        }

        const newAccessToken = await refreshAccessToken();
        request.headers.Authorization = `Bearer ${newAccessToken}`;
    } else if (accessToken) {
        request.headers.Authorization = `Bearer ${accessToken}`;
    }

    return request;
};

export const onRequestRejected = error => {
    return Promise.reject(error);
};

const onResponseFulfilled = response => {
    if (response.data?.data?.message) toast.success(response.data?.data?.message, { toastId: "custom-id-yes" });

    return response;
};

const onResponseRejected = async error => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // Handle 401 Unauthorized errors by attempting token refresh
    if (status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
            const newAccessToken = await refreshAccessToken();
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return axiosInstance(originalRequest);
        } catch (refreshError) {
            return Promise.reject(refreshError);
        }
    }

    // For all other errors, reject with original error
    return Promise.reject(error);
};

const axiosInstance = axios.create({
    baseURL: BASE_URL,
    timeout: REQUEST_TIMEOUT,
    headers: {
        "Content-Type": "application/json",
    },
});

axiosInstance.interceptors.request.use(onRequestFulfilled, onRequestRejected);
axiosInstance.interceptors.response.use(onResponseFulfilled, onResponseRejected);

export default axiosInstance;
